import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { CreatorService } from "./shared/data/services/creator.service";

export type CreatorStep = "location" | "storage" | "period" | "orderForm";

/** Kroki kreatora w kolejności i ich adresy. */
const CREATOR_STEPS: ReadonlyArray<{ step: CreatorStep; path: string }> = [
  { step: "location", path: "/creator" },
  { step: "storage", path: "/creator/storage" },
  { step: "period", path: "/creator/period" },
  { step: "orderForm", path: "/creator/orderForm" },
];

/**
 * Najdalszy krok, do którego wystarcza stan w sessionStorage:
 * - magazyn – wybrana lokalizacja i założona rezerwacja,
 * - okres – zapisany wybór magazynu,
 * - formularz – zapisany okres (sumy rezerwacji z odpowiedzi kroku okresu;
 *   zmiana lokalizacji albo magazynu je usuwa, więc okres trzeba zatwierdzić
 *   ponownie).
 */
function reachableStepIndex(creatorService: CreatorService): number {
  const reservation = creatorService.getSessionStorageItem("reservationSession");
  if (!creatorService.getSessionStorageItem("selectedLocation") || !reservation?.id) {
    return 0;
  }
  const details = creatorService.getSessionStorageItem("reservationDetails", []);
  if (!Array.isArray(details) || details.length === 0) {
    return 1;
  }
  if (reservation.totalNettoPrice == null) {
    return 2;
  }
  return 3;
}

/**
 * Wejście na krok kreatora – także odświeżenie strony albo adres wpisany
 * ręcznie – tylko ze stanem z poprzednich kroków. Inaczej przekierowanie na
 * najdalszy krok, do którego ten stan wystarcza (np. formularz bez zapisanego
 * okresu → okres). Na wcześniejsze kroki wolno wracać zawsze.
 */
export const creatorStepGuard =
  (step: CreatorStep): CanActivateFn =>
  () => {
    const target = CREATOR_STEPS.findIndex((item) => item.step === step);
    const reachable = reachableStepIndex(inject(CreatorService));
    return target <= reachable
      ? true
      : inject(Router).createUrlTree([CREATOR_STEPS[reachable].path]);
  };
